import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';

import { Declarant, LowValuesDeclarantDataSource } from '@e-origin/shared';

import { useEditDeclarant } from '@admin/hooks';
import { DEFAULT_TRANSIT_PROCESS_ID_SEQUENCE_NUMBER } from '@constants';

const externalSystemCustomsSchema = z.object({
  systemName: z.string().optional().nullable(),
  systemProtocol: z.string().optional().nullable(),
  hostname: z.string().optional().nullable(),
  port: z.number().optional().nullable(),
  username: z.string().optional().nullable(),
  password: z.string().optional().nullable(),
  incomingPath: z.string().optional().nullable(),
  outgoingPath: z.string().optional().nullable(),
  incomingPathBegateFreed: z.string().optional().nullable(),
  incomingPathBegateBlocked: z.string().optional().nullable(),
  incomingPathBegateError: z.string().optional().nullable(),
  outgoingPathBeGate: z.string().optional().nullable(),
});

const customsAuthenticationSchema = z.object({
  transitProcessIdSequenceNumber: z.string().regex(/^\d{2}$/, { message: 'Must be exactly 2 digits' }),
});

const editDeclarantSchema = z.object({
  name: z.string().optional().nullable(),
  accountName: z.string().min(1, { message: 'Account name is required' }),
  language: z.string().optional().nullable(),
  deleted: z.boolean().optional().nullable(),
  default: z.boolean().optional().nullable(),
  invoiceCounterReset: z.boolean().optional().nullable(),
  declarationCounterReset: z.boolean().optional().nullable(),
  nlSequenceReset: z.boolean().optional().nullable(),
  functionalFeatures: z.object({
    autoB2CSplitLVHV: z.boolean().optional().nullable(),
    lowValuesDeclarantDataSource: z.nativeEnum(LowValuesDeclarantDataSource).default(LowValuesDeclarantDataSource.DECLARANT),
    showNewLvReportColumns: z.boolean().optional().nullable(),
  }),
  externalSystemCustoms: externalSystemCustomsSchema,
  customsAuthentication: customsAuthenticationSchema,
});

export const useEditDeclarantFormInitializer = ({ declarant }) => {
  const editDeclarantMutation = useEditDeclarant();

  const buildInitialValues = (d: Declarant) => ({
    name: d?.name || '',
    accountName: d?.accountName || '',
    language: d?.language || '',
    deleted: d?.deleted || false,
    default: d?.default || false,
    invoiceCounterReset: false,
    declarationCounterReset: false,
    nlSequenceReset: false,
    functionalFeatures: {
      autoB2CSplitLVHV: d?.functionalFeatures?.autoB2CSplitLVHV || false,
      lowValuesDeclarantDataSource: d?.functionalFeatures?.lowValuesDeclarantDataSource || LowValuesDeclarantDataSource.DECLARANT,
      showNewLvReportColumns: d?.functionalFeatures?.showNewLvReportColumns || false,
    },
    externalSystemCustoms: {
      systemName: d?.externalSystemCustoms?.systemName || '',
      systemProtocol: d?.externalSystemCustoms?.systemProtocol || '',
      hostname: d?.externalSystemCustoms?.hostname || '',
      port: d?.externalSystemCustoms?.port || null,
      username: d?.externalSystemCustoms?.username || '',
      password: d?.externalSystemCustoms?.password || '',
      incomingPath: d?.externalSystemCustoms?.incomingPath || '',
      outgoingPath: d?.externalSystemCustoms?.outgoingPath || '',
      incomingPathBegateFreed: d?.externalSystemCustoms?.incomingPathBegateFreed || '',
      incomingPathBegateBlocked: d?.externalSystemCustoms?.incomingPathBegateBlocked || '',
      incomingPathBegateError: d?.externalSystemCustoms?.incomingPathBegateError || '',
      outgoingPathBeGate: d?.externalSystemCustoms?.outgoingPathBeGate || '',
    },
    customsAuthentication: {
      transitProcessIdSequenceNumber: d?.customsAuthentication?.transitProcessIdSequenceNumber || DEFAULT_TRANSIT_PROCESS_ID_SEQUENCE_NUMBER,
    },
  });

  const onSubmit = async (values) => {
    await editDeclarantMutation.mutateAsync({
      declarant: {
        ...values,
        externalSystemCustoms: { ...values.externalSystemCustoms, systemName: values.externalSystemCustoms.systemName || '' },
        updatedAt: null,
      },
      declarantId: declarant._id,
    });
  };

  const form = useForm({
    initialValues: buildInitialValues(declarant),
    validate: zodResolver(editDeclarantSchema),
    validateInputOnBlur: true,
  });

  return {
    form,
    buildInitialValues,
    onSubmit,
  };
};
